import type { Option } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Card,
    FormGroup,
    FormParagraph,
    FormSection,
    Radio,
    RadioGroup,
    Select,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { OrganizationTypeWithLinkedProfessions } from '@common/enums';
import type { EmploymentAdd } from '@common/interfaces/employmentAdd';
import type { FieldRequirementsBasedOnProfession } from '@frontend/utils/checkRequiredAndAllowedAccountCreationFieldsPerProfession';
import { SelectProfession } from '@frontend/components/elements/SelectProfession';
import { SpecialismSelector } from '@frontend/components/elements/SpecialismSelector';
import {
    checkRequiredAndAllowedAccountCreationFieldsPerProfession,
    fieldRequirementsBasedOnProfessionInitialState,
} from '@frontend/utils/checkRequiredAndAllowedAccountCreationFieldsPerProfession';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import { loadSupervisorOptions } from '@frontend/utils/loadSupervisorOptions';
import type { IIdentifierDeclaration } from '../../../../common/interfaces/identifierDeclaration';
import type { IRegistrationCodesAdd } from '../../../../common/interfaces/practitionerAdd';
import type { ValidationErrors } from '../../../utils/validation';
import { Profession, professionsByOrganizationType } from '../../../../common/enums/profession';
import { getSelectedLanguage } from '../../../translate';
import { getValidationErrorTranslation } from '../../../utils/getValidationErrorTranslation';
import { DateInput } from '../../elements/common';
import { IdentifierDeclaration } from '../../userDataManagement/IdentifierDeclaration';

interface IEmploymentDetailsAddFormProps {
    employment: EmploymentAdd;
    errors: ValidationErrors;
    handleChangeEmployment: (update: Partial<EmploymentAdd>) => void;
    handleChangeIdentifierDeclaration: <K extends keyof IIdentifierDeclaration>(
        name: K,
        value: IIdentifierDeclaration[K]
    ) => void;
    handleChangePractitioner: (update: Partial<IRegistrationCodesAdd>) => void;
    organizationId?: string;
    organizationType: OrganizationTypeWithLinkedProfessions | null;
    practitionerRegistrationCodes: IRegistrationCodesAdd;
    practitionerWillUseCS: boolean;
    profilePractitionerRegistrationCodes: IRegistrationCodesAdd;
}

export function EmploymentDetailsAddForm(props: Readonly<IEmploymentDetailsAddFormProps>) {
    const [fieldRequirementsBasedOnProfession, setFieldRequirementsBasedOnProfession] =
        useState<FieldRequirementsBasedOnProfession>(fieldRequirementsBasedOnProfessionInitialState);
    const [supervisorOptions, setSupervisorOptions] = useState<Option[]>([]);

    const { frontendSettings } = useFrontendSettings();

    const translate = getSelectedLanguage();

    useEffect(() => {
        if (
            !(
                props?.organizationType &&
                Object.keys(professionsByOrganizationType[props.organizationType]).includes(
                    props.employment.profession ?? ''
                )
            )
        ) {
            props.handleChangeEmployment({ profession: null });
        }
    }, [props.organizationType]);

    useEffect(() => {
        updateModalFieldsBasedOnProfession();
    }, [props.employment.profession]);

    useEffect(() => {
        loadSupervisors();
    }, [props.organizationId]);

    return (
        <Card>
            <Card.Header title={translate.organization.practitionerDataAtOrganization} />
            <Card.Body>
                <FormSection>
                    <FormGroup
                        hasErrors={props.errors.has('employment.profession')}
                        isRequired={true}
                        label={translate.common.profession}
                    >
                        <SelectProfession
                            error={getValidationErrorTranslation(props.errors.get('employment.profession'))}
                            onChange={(profession: Profession | null) => {
                                const updateEmployment = { ...props.employment, profession };
                                props.handleChangeEmployment(updateEmployment);
                            }}
                            organizationType={props.organizationType}
                            placeholder=""
                            profession={props.employment.profession}
                        />
                    </FormGroup>
                    {fieldRequirementsBasedOnProfession.isIdentifierRegistrationKpRequired && (
                        <FormGroup
                            hasErrors={props.errors.has('practitioner.identifierRegistrationKp')}
                            isRequired={true}
                            label={translate.identifierDeclarationOrRegistration.identifierRegistrationKp}
                        >
                            {!props.profilePractitionerRegistrationCodes.identifierRegistrationKp ? (
                                <TextInput
                                    error={getValidationErrorTranslation(
                                        props.errors.get('practitioner.identifierRegistrationKp')
                                    )}
                                    onChange={(e) =>
                                        props.handleChangePractitioner({
                                            identifierRegistrationKp: e.target.value ? e.target.value : null,
                                        })
                                    }
                                    type="text"
                                    value={props.practitionerRegistrationCodes.identifierRegistrationKp ?? ''}
                                />
                            ) : (
                                <FormParagraph>
                                    {props.practitionerRegistrationCodes.identifierRegistrationKp}
                                </FormParagraph>
                            )}
                        </FormGroup>
                    )}
                    {fieldRequirementsBasedOnProfession.isSupervisorAllowed && (
                        <FormGroup
                            hasErrors={props.errors.has('employment.identifierDeclarationPractitionerId')}
                            isRequired={
                                fieldRequirementsBasedOnProfession.isAgbCodeOrSupervisorRequired
                                    ? !props.practitionerRegistrationCodes.identifierDeclaration
                                    : fieldRequirementsBasedOnProfession.isSupervisorRequired
                            }
                            label={
                                props.employment.profession === Profession.generalPractitionerWaarnemer
                                    ? translate.accountRequest.practiceHolder
                                    : translate.supervisors.supervisor
                            }
                        >
                            <Select
                                error={getValidationErrorTranslation(
                                    props.errors.get('employment.identifierDeclarationPractitionerId')
                                )}
                                isClearable={true}
                                onChange={(option) => {
                                    props.handleChangeEmployment({
                                        identifierDeclarationPractitionerId: option?.value ?? null,
                                    });
                                }}
                                options={supervisorOptions}
                                placeholder=""
                                value={supervisorOptions.find(
                                    (supervisor) =>
                                        supervisor.value === props.employment.identifierDeclarationPractitionerId
                                )}
                            />
                        </FormGroup>
                    )}
                    {fieldRequirementsBasedOnProfession.isSpecialismFieldRequired && (
                        <SpecialismSelector
                            errors={props.errors}
                            onChange={(value: string | null) => props.handleChangeEmployment({ specialismCode: value })}
                            value={props.employment.specialismCode ?? null}
                        />
                    )}
                    {(fieldRequirementsBasedOnProfession.isAgbCodeAllowed ||
                        fieldRequirementsBasedOnProfession.isAgbCodeRequired) && (
                        <IdentifierDeclaration
                            errors={props.errors}
                            hidePlaceholder={true}
                            identifierDeclarationCodeLabel={'practitioner.identifierDeclaration'}
                            isRequired={
                                fieldRequirementsBasedOnProfession.isAgbCodeOrSupervisorRequired
                                    ? !props.employment.identifierDeclarationPractitionerId
                                    : fieldRequirementsBasedOnProfession.isAgbCodeRequired
                            }
                            onChange={async (key, value) => props.handleChangeIdentifierDeclaration(key, value)}
                            readOnly={!!props.profilePractitionerRegistrationCodes.identifierDeclaration}
                            value={props.practitionerRegistrationCodes}
                        />
                    )}
                    {fieldRequirementsBasedOnProfession.isIdentifierRegistrationBigRequired && (
                        <FormGroup
                            hasErrors={props.errors.has('practitioner.identifierRegistrationBig')}
                            label={translate.identifierDeclarationOrRegistration.identifierRegistrationBig}
                        >
                            {!props.profilePractitionerRegistrationCodes.identifierRegistrationBig ? (
                                <TextInput
                                    error={getValidationErrorTranslation(
                                        props.errors.get('practitioner.identifierRegistrationBig')
                                    )}
                                    onChange={(e) =>
                                        props.handleChangePractitioner({
                                            identifierRegistrationBig: e.target.value ? e.target.value : null,
                                        })
                                    }
                                    type="text"
                                    value={props.practitionerRegistrationCodes.identifierRegistrationBig ?? ''}
                                />
                            ) : (
                                <FormParagraph>
                                    {props.practitionerRegistrationCodes.identifierRegistrationBig}
                                </FormParagraph>
                            )}
                        </FormGroup>
                    )}
                    {fieldRequirementsBasedOnProfession.isZorgmailNummerFieldAllowed && (
                        <FormGroup
                            hasErrors={props.errors.has('employment.zorgmailNummer')}
                            isRequired={fieldRequirementsBasedOnProfession.isZorgmailNummerFieldRequired}
                            label={translate.employment.zorgmailNummer}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(props.errors.get('employment.zorgmailNummer'))}
                                onChange={(e) =>
                                    props.handleChangeEmployment({
                                        zorgmailNummer: e.target.value ? e.target.value : null,
                                    })
                                }
                                value={props.employment.zorgmailNummer ?? ''}
                            />
                        </FormGroup>
                    )}
                    <FormGroup
                        hasErrors={props.errors.has('employment.emailAddress')}
                        isRequired={true}
                        label={translate.employment.workEmail}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(props.errors.get('employment.emailAddress'))}
                            onChange={(e) => props.handleChangeEmployment({ emailAddress: e.target.value || null })}
                            type="text"
                            value={props.employment.emailAddress ?? ''}
                        />
                    </FormGroup>
                    {frontendSettings.organizationPayoutManagerEnabled && (
                        <FormGroup
                            hasErrors={props.errors.has('employment.isOrganizationManager')}
                            isRequired={true}
                            label={translate.employment.organizationManager}
                            tooltipText={translate.employment.organizationManagerExplanation}
                        >
                            <RadioGroup
                                error={getValidationErrorTranslation(
                                    props.errors.get('employment.isOrganizationManager')
                                )}
                                name="employment.isOrganizationManager"
                                onChange={(event) => {
                                    props.handleChangeEmployment({
                                        isOrganizationManager: event.target.value === 'true',
                                    });
                                }}
                                value={props.employment.isOrganizationManager?.toString() ?? ''}
                            >
                                <Radio
                                    key="true"
                                    label={translate.common.yes}
                                    value="true"
                                />
                                <Radio
                                    key="false"
                                    label={translate.common.no}
                                    value="false"
                                />
                            </RadioGroup>
                        </FormGroup>
                    )}
                    <FormGroup
                        hasErrors={props.errors.has('employment.startDate')}
                        isRequired
                        label={translate.common.startingDate}
                    >
                        <DateInput
                            error={props.errors.get('employment.startDate')}
                            hasMaxWidth={true}
                            minDate={DateTime.now().format('YYYY-MM-DD')}
                            onChange={(value) => props.handleChangeEmployment({ startDate: value })}
                            placeholder={translate.common.dateFormat}
                            size="large"
                            value={props.employment.startDate}
                            variant="outline"
                        />
                    </FormGroup>
                </FormSection>
            </Card.Body>
        </Card>
    );

    async function loadSupervisors(searchText?: string) {
        if (props.organizationId) {
            const supervisors = await loadSupervisorOptions(props.organizationId, searchText);
            setSupervisorOptions(supervisors);
        }
    }

    function updateModalFieldsBasedOnProfession() {
        const fieldRequirements: FieldRequirementsBasedOnProfession =
            checkRequiredAndAllowedAccountCreationFieldsPerProfession(
                props.employment.profession,
                props.organizationType
            );
        setFieldRequirementsBasedOnProfession(fieldRequirements);

        props.handleChangeEmployment({
            identifierDeclarationPractitionerId: fieldRequirements.isSupervisorRequired
                ? props.employment.identifierDeclarationPractitionerId
                : null,
            specialismCode: fieldRequirements.isSpecialismFieldRequired ? props.employment.specialismCode : null,
            zorgmailNummer: fieldRequirements.isZorgmailNummerFieldAllowed ? props.employment.zorgmailNummer : null,
        });

        props.handleChangePractitioner({
            identifierDeclaration: fieldRequirements.isAgbCodeAllowed
                ? (props.practitionerRegistrationCodes.identifierDeclaration ??
                  props.profilePractitionerRegistrationCodes.identifierDeclaration)
                : null,
            identifierRegistrationBig: fieldRequirements.isIdentifierRegistrationBigRequired
                ? (props.practitionerRegistrationCodes.identifierRegistrationBig ??
                  props.profilePractitionerRegistrationCodes.identifierRegistrationBig)
                : null,
            identifierRegistrationKp: fieldRequirements.isIdentifierRegistrationKpRequired
                ? (props.practitionerRegistrationCodes.identifierRegistrationKp ??
                  props.profilePractitionerRegistrationCodes.identifierRegistrationKp)
                : null,
        });
    }
}
